import  { useState, useEffect } from 'react';
import { AlertCircle } from 'lucide-react';

interface AlertProps {
    type?: 'success' | 'error' | 'info' | 'warning';
    title?: string;
    description?: string;
    autoDismiss?: boolean; // Auto-dismiss after a certain time
    dismissTime?: number; // Dismiss time in milliseconds
    onDismiss?: () => void; // Callback when dismissed
}

export const Alert: React.FC<AlertProps> = ({
                                                type = 'info',
                                                title,
                                                description,
                                                autoDismiss = false,
                                                dismissTime = 5000,
                                                onDismiss,
                                            }) => {
    const [isVisible, setIsVisible] = useState(true);

    useEffect(() => {
        if (autoDismiss) {
            const timer = setTimeout(() => {
                setIsVisible(false);
                onDismiss?.(); // Trigger callback if provided
            }, dismissTime);
            return () => clearTimeout(timer); // Cleanup on unmount
        }
    }, [autoDismiss, dismissTime, onDismiss]);

    if (!isVisible) {
        return null;
    }

    const typeStyles = {
        success: 'bg-green-100 text-green-800 border-green-200',
        error: 'bg-red-100 text-red-800 border-red-200',
        info: 'bg-blue-100 text-blue-800 border-blue-200',
        warning: 'bg-yellow-100 text-yellow-800 border-yellow-200',
    };

    return (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
            <div className={`flex flex-col items-start p-6 rounded-md shadow-lg ${typeStyles[type]}`}>
                <div className="flex items-center mb-4">
                    <AlertCircle className="mr-3 h-6 w-6" />
                    {title && <h4 className="font-bold text-lg">{title}</h4>}
                </div>
                {description && <p className="mb-4">{description}</p>}
                <button
                    onClick={() => {
                        setIsVisible(false);
                        onDismiss?.();
                    }}
                    className="ml-auto px-4 py-2 rounded bg-gray-700 text-white hover:bg-gray-800"
                >
                    Close
                </button>
            </div>
        </div>
    );
};
